import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { throttle } from 'lodash';
import uniqueId from 'lodash/uniqueId';
import Trans from '@/components/shared/Trans';
import translations from '../Dashboard.trans.json';
import {
  increaseRefreshCount,
  setEdition,
  setInitialEditionData,
} from '@/store/slices/components/dashboard/DashboardSlice';
import MixPanelService from '@/services/MixPanelService';
import { buildBackendStructureTabs } from '@/components/Dashboard/utils/dashboardConfig';
import { putDashboardConfig } from '@/store/slices/components/dashboard/service';
import { setDashboardConfig } from '@/store/slices/UserPreferencesSlice';
import {
  editToast,
  showToast,
} from '@/store/slices/components/toast/ToastSlice';

function useDashboardHeader() {
  const dispatch = useDispatch();
  const { edition } = useSelector((state) => state.DashboardSlice);
  const { dashboardConfig } = useSelector(
    (state) => state.UserPreferencesSlice
  );
  const isAdmin = useSelector(
    (state) => state.AuthSlice.permissions.is_company_admin
  );
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [didEditionChange, setDidEditionChange] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    const FIVE_MINUTES = 300000;

    const timer = setTimeout(() => {
      setShowRefreshButton(true);
    }, FIVE_MINUTES);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const sections = Object.values(edition.tabs).flatMap(
      (tab) => tab.sections || []
    );

    setDidEditionChange(sections.some((section) => section.dirty));
  }, [edition.tabs]);

  const handleClickRefresh = useCallback(() => {
    try {
      dispatch(increaseRefreshCount());
      MixPanelService.track({
        event_name: 'platform_dashboard_refresh',
      });
    } catch (error) {
      console.log(error);
    }
  }, [dispatch]);

  const throttledClick = useCallback(
    throttle(handleClickRefresh, 1000, { leading: true, trailing: false }),
    [handleClickRefresh]
  );

  const toggleEditionMode = () => {
    if (!edition.active) {
      MixPanelService.track({
        event_name: 'platform_dashboard_custom_adoption',
        extra_info: {
          'user-type': isAdmin ? 'Admin' : 'No admin',
        },
      });
    }

    dispatch(setEdition({ key: 'active', value: !edition.active }));
  };

  const trackEntries = () =>
    edition.entriesToTrack.forEach((entry) =>
      MixPanelService.track({
        event_name: entry.event,
        extra_info: {
          [entry.type]: entry.card,
          tab: entry.tab,
          'user-type': isAdmin ? 'Admin' : 'No admin',
        },
      })
    );

  const trackClickOnSave = () =>
    MixPanelService.track({
      event_name: 'platform_dashboard_custom_save',
      extra_info: {
        'user-type': isAdmin ? 'Admin' : 'No admin',
      },
    });

  const handleSave = () => {
    const toastId = uniqueId('toast_');
    dispatch(
      showToast({
        id: toastId,
        text: Trans(translations, 'Saving'),
        type: 'loading',
        time: 15000,
      })
    );
    const payloadToSend = buildBackendStructureTabs(edition.tabs, {
      onlyIncludeDirty: true,
    });
    const configToAssign = buildBackendStructureTabs(edition.tabs);

    dispatch(setDashboardConfig(configToAssign));

    trackClickOnSave();
    trackEntries();

    dispatch(putDashboardConfig({ payload: payloadToSend })).then(() =>
      dispatch(
        editToast({
          id: toastId,
          text: Trans(translations, 'SavedChanges'),
          type: 'success',
          time: 3000,
        })
      )
    );
  };

  const handleCancel = () => {
    dispatch(setInitialEditionData(dashboardConfig));
    toggleEditionMode();

    MixPanelService.track({
      event_name: 'platform_dashboard_custom_cancel',
      extra_info: {
        'user-type': isAdmin ? 'Admin' : 'No admin',
      },
    });
  };

  const handleOpenConfirmationModal = () => {
    setShowConfirmModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmModal(false);
  };

  const handleConfirmCancel = () => {
    setShowConfirmModal(false);
    handleCancel();
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    handleSave();
  };

  return {
    showRefreshButton,
    didEditionChange,
    showConfirmModal,
    handleClickRefresh: throttledClick,
    toggleEditionMode,
    handleCancel,
    handleSave,
    handleOpenConfirmationModal,
    handleCloseConfirmationModal,
    handleConfirmCancel,
    handleConfirmSave,
  };
}

export default useDashboardHeader;
